import { ResponsiveChoropleth } from '@nivo/geo'
import styled from '@emotion/styled'
import React, { useEffect, useRef, useState } from 'react'
import { LegendProps } from '@nivo/legends'
import { Spinner, useTheme } from '@ss/meta-core-components'
import { getScaleAndProjection, ScaleAndProjection } from './utils'
import { IDimension } from '@types'
import useRest from '../../hooks/useRest'
import { initTheme } from '@utils'

interface Props {
  data: any[]
  dimensions?: IDimension
  dataCy?: string
  domain?: [number, number]
  loadingSpinner?: JSX.Element
}

interface Features {
  features: any[]
  type:
    | 'FeatureCollection'
    | 'Feature'
    | 'Polygon'
    | 'MultiPolygon'
    | 'Point'
    | 'MultiPoint'
    | 'LineString'
    | 'MultiLineString'
    | 'GeometryCollection'
  bbox: [number, number, number, number, number, number]
}

/**
 * Componente chart di tipo geo heat map
 * @param props.data {any[]} dati da visualizzare
 * @param props.dimensions {IDimension= } dimensioni del container
 * @param props.dataCy {string=} support test e2e
 * @param props.domain {number[] | undefined} array contenente il valore minimo e il valore massimo che può prendere il grafico
 * @param props.loadingSpinner {JSX.Element=} componente custo per lo spinner
 * @component
 */
export const Geo: React.FC<Props> = (props) => {
  const {
    data,
    dataCy = '',
    dimensions = { height: '375px', width: '100%' },
    domain,
    loadingSpinner,
  } = props
  const chartContainer = useRef<HTMLDivElement | null>(null)
  const [features, setFeatures] = useState<Features>()
  const [projection, setProjection] = useState<ScaleAndProjection>()
  const [chartProps, setChartProps] = useState<any>({
    margin: { top: 0, right: 0, bottom: 0, left: 0 },
    unknownColor: '#AAAEB0',
    label: 'properties.name',
    valueFormat: '.2s',
  })

  const { post } = useRest<Features>('/get_geo_file')

  useEffect(() => {
    post({
      no_param: 'no param',
    }).then((res) => {
      setFeatures(res)
    })
  }, [])
  //FIXME: non funziona la projection se devi rirenderizzare le dimensioni del grafico in esecuzione del app  (esempio hot reload react dev )

  useEffect(() => {
    if (!features) return
    if (
      !chartContainer.current ||
      !chartContainer.current.offsetWidth ||
      !chartContainer.current.offsetHeight
    )
      return
    const data = getScaleAndProjection(
      features,
      chartContainer.current.offsetWidth,
      chartContainer.current.offsetHeight
    )
    setProjection({ ...data })
  }, [
    chartContainer.current,
    chartContainer.current?.offsetHeight,
    chartContainer.current?.offsetWidth,
    features,
  ])

  useEffect(() => {
    if (!projection || !data) return
    setChartProps((old: any) => ({
      ...old,
      data: [...data],
      projectionScale: projection.scale,
      projectionTranslation: projection.translate,
    }))
  }, [data, projection])

  const { type } = useTheme()

  const legends: LegendProps[] = [
    {
      anchor: 'bottom-right',
      direction: 'column',
      justify: true,
      translateX: -10,
      translateY: -15,
      itemsSpacing: 8,
      itemWidth: 120,
      itemHeight: 18,
      itemTextColor: '#999',
      itemDirection: 'left-to-right',
      itemOpacity: 0.85,
      symbolSize: 18,
      symbolShape: 'square',
      effects: [
        {
          on: 'hover',
          style: {
            itemOpacity: 1,
          },
        },
      ],
    },
  ]

  return (
    <Container
      height={dimensions.height || '375px'}
      width={dimensions.width || '100%'}
      data-cy={`${dataCy}-geo-heat-map`}
      ref={chartContainer}
    >
      {!features || !projection || !domain ? (
        <Loading>
          <Loading>
            {loadingSpinner ? (
              loadingSpinner
            ) : (
              <Spinner data-cy={`${dataCy}-bar-loading`} />
            )}
          </Loading>
          <Loading>Updating Geo chart...</Loading>
        </Loading>
      ) : (
        <ResponsiveChoropleth
          {...chartProps}
          legends={legends}
          theme={initTheme(type)}
          features={features.features}
          domain={domain}
        />
      )}
    </Container>
  )
}

const Container = styled.div<{ height: string; width: string }>`
  height: ${({ height }) => height};
  width: ${({ width }) => width};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
`
const Loading = styled.div`
  display: grid;
  place-self: center;
  gap: 10px;
`
